import { useEffect, useRef, useState } from "react";
import { Photo, photoUrl } from "./components";
import { ChevronLeft, ChevronRight, X, ZoomIn } from "lucide-react";

function Lightbox({ src, n, onStep, onClose }) {
  const start = useRef(null);
  useEffect(() => {
    const key = (e) => { if (e.key === "Escape") onClose(); if (e.key === "ArrowLeft") onStep(-1); if (e.key === "ArrowRight") onStep(1); };
    document.addEventListener("keydown", key);
    return () => document.removeEventListener("keydown", key);
  }, [onClose, onStep]);
  const end = (e) => { const dx = e.changedTouches[0].clientX - (start.current ?? e.changedTouches[0].clientX); if (n > 1 && Math.abs(dx) > 40) onStep(dx < 0 ? 1 : -1); };
  const stop = (fn) => (e) => { e.stopPropagation(); fn(); };
  return (
    <div role="dialog" aria-modal="true" className="fixed inset-0 z-[70] flex items-center justify-center bg-[#0f2a3f]/95 p-4 backdrop-blur-sm" onClick={onClose} onTouchStart={(e) => { start.current = e.touches[0].clientX; }} onTouchEnd={end} data-testid="lightbox">
      <button type="button" aria-label="Cerrar" data-testid="lightbox-close" onClick={stop(onClose)} className="absolute right-4 top-4 rounded-full bg-white/10 p-2 text-white"><X className="h-6 w-6" /></button>
      {n > 1 && <button type="button" aria-label="Anterior" data-testid="lightbox-prev" onClick={stop(() => onStep(-1))} className="absolute left-4 rounded-full bg-white/10 p-2 text-white"><ChevronLeft className="h-6 w-6" /></button>}
      <img src={src} alt="" className="max-h-full max-w-full object-contain" onClick={(e) => e.stopPropagation()} />
      {n > 1 && <button type="button" aria-label="Siguiente" data-testid="lightbox-next" onClick={stop(() => onStep(1))} className="absolute right-4 rounded-full bg-white/10 p-2 text-white"><ChevronRight className="h-6 w-6" /></button>}
    </div>
  );
}

export default function ProductGallery({ photos, title }) {
  const list = photos || [];
  const n = list.length;
  const [i, setI] = useState(0);
  const [zoom, setZoom] = useState(false);
  const main = useRef(null);
  const thumbs = useRef([]);
  const settle = useRef(null);
  useEffect(() => setI(0), [photos]);
  useEffect(() => { thumbs.current[i]?.scrollIntoView?.({ inline: "nearest", block: "nearest" }); }, [i]);
  const go = (k) => { setI(k); const el = main.current; el?.scrollTo?.({ left: k * (el.clientWidth || 0), behavior: "smooth" }); };
  const onScroll = (e) => {
    const el = e.currentTarget;
    clearTimeout(settle.current);
    settle.current = setTimeout(() => { if (el.clientWidth) setI(Math.min(n - 1, Math.max(0, Math.round(el.scrollLeft / el.clientWidth)))); }, 80);
  };
  const step = (d) => go((i + d + n) % n);
  return (
    <div data-testid="gallery">
      <div className="relative">
        <div ref={main} onScroll={onScroll} className="flex snap-x snap-mandatory overflow-x-auto rounded-xl bg-[#f6f8fa] [scrollbar-width:none]" data-testid="gallery-track">
          {n ? list.map((p, k) => (
            <button key={`${p}-${k}`} type="button" data-testid={`gallery-main-${k}`} onClick={() => { setI(k); setZoom(true); }} className="block aspect-square w-full shrink-0 snap-center">
              <Photo id={p} alt={k === 0 ? title : `${title} · ${k + 1}`} w={800} width={800} height={800} priority={k === 0} className="h-full w-full p-3" />
            </button>
          )) : <div className="aspect-square w-full"><Photo id={null} alt={title} className="h-full w-full p-3" /></div>}
        </div>
        {n > 0 && <span className="pointer-events-none absolute bottom-3 right-3 flex items-center gap-1.5 rounded-full bg-[#0f2a3f]/90 px-3 py-1.5 text-xs font-semibold text-white shadow" data-testid="gallery-counter"><ZoomIn className="h-3.5 w-3.5" />{`${i + 1}/${n}`}</span>}
      </div>
      {n > 1 && (
        <div className="mt-3 flex snap-x gap-2 overflow-x-auto pb-1 [scrollbar-width:none]" data-testid="gallery-thumbs">
          {list.map((p, k) => (
            <button key={`${p}-${k}`} ref={(el) => { thumbs.current[k] = el; }} type="button" aria-label={`Foto ${k + 1} de ${n}`} aria-current={k === i ? "true" : undefined} onClick={() => go(k)} data-testid={`thumb-${k}`} className={`h-14 w-14 shrink-0 snap-start overflow-hidden rounded-lg border-2 sm:h-16 sm:w-16 ${k === i ? "border-emerald-600 ring-2 ring-emerald-600/20" : "border-slate-200 hover:border-slate-400"}`}>
              <Photo id={p} w={160} width={160} height={160} className="h-full w-full" />
            </button>
          ))}
        </div>
      )}
      {zoom && n > 0 && <Lightbox src={photoUrl(list[i])} n={n} onStep={step} onClose={() => setZoom(false)} />}
    </div>
  );
}
